import React from 'react';
import { Helmet } from 'react-helmet';
import {
  generateOrganizationSchema,
  generateWebsiteSchema,
  generateWebPageSchema,
  combineSchemas
} from '../../utils/schemaGenerator';

const DEFAULT_DESCRIPTION = 'The future of Interviewing';

const SEO = ({
  title = 'Nora AI - AI Mock Interview Preparation Platform',
  description = DEFAULT_DESCRIPTION,
  url = 'https://interview.norahq.com',
  image = 'https://interview.norahq.com/images/logo/nora-ai-logo.png',
  type = 'website',
  schemas = [],
  keywords = 'AI mock interview, interview prep, practice interviews, technical interview, behavioral questions, job interview practice',
  author = 'Nora AI Team',
  openGraph = {},
  customMetaTags = []
}) => {
  // Generate base schemas
  const organizationSchema = generateOrganizationSchema();
  const websiteSchema = generateWebsiteSchema();
  const webPageSchema = generateWebPageSchema(title, description, url);

  // Combine all schemas
  const allSchemas = combineSchemas([
    organizationSchema,
    websiteSchema,
    webPageSchema,
    ...schemas
  ]);

  const {
    title: ogTitle,
    description: ogDescription,
    url: ogUrl,
    image: ogImage,
    type: ogType,
    siteName: ogSiteName
  } = openGraph;

  const resolvedOgTitle = ogTitle || title;
  const resolvedOgDescription = ogDescription || description;
  const resolvedOgUrl = ogUrl || url;
  const resolvedOgImage = ogImage || image;
  const resolvedOgType = ogType || type;
  const resolvedOgSiteName = ogSiteName || 'Nora AI';

  const safeCustomMetaTags = Array.isArray(customMetaTags)
    ? customMetaTags.filter((tag) => tag && tag.content)
    : [];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <link rel="canonical" href={url} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={resolvedOgTitle} />
      <meta property="og:description" content={resolvedOgDescription} />
      <meta property="og:url" content={resolvedOgUrl} />
      <meta property="og:image" content={resolvedOgImage} />
      <meta property="og:type" content={resolvedOgType} />
      <meta property="og:site_name" content={resolvedOgSiteName} />

      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />

      {safeCustomMetaTags.map((tag, index) => {
        const key = `seo-custom-meta-${tag.name || tag.property || index}`;
        if (tag.name) {
          const { name, content, ...rest } = tag;
          return <meta key={key} name={name} content={content} {...rest} />;
        }
        if (tag.property) {
          const { property, content, ...rest } = tag;
          return <meta key={key} property={property} content={content} {...rest} />;
        }
        if (tag.httpEquiv) {
          const { httpEquiv, content, ...rest } = tag;
          return <meta key={key} httpEquiv={httpEquiv} content={content} {...rest} />;
        }
        return null;
      })}

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(allSchemas, null, 2)}
      </script>
    </Helmet>
  );
};

export default SEO;
